import { Modal, Button } from '@mui/material';
import { FormikProps } from 'formik';
import { useRef } from 'react';
import { Footer, ModalBox, ModalH2 } from '../../pages/Styles/ModalStyle';
import FormBuilder from './FormBuilder';
import { Item } from './InputSelect';

export interface ParametrosRelatorio {
  dataInicio: string;
  dataFim: string;
  setor?: number | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  titulo: string;
  /** Datas que a tela já tinha preenchidas. Entram como sugestão, não como imposição. */
  dataInicioInicial?: string;
  dataFimInicial?: string;
  /**
   * Setores do cadastro. Ausente (ou vazio) = o relatório não tem esse filtro e o combo
   * nem aparece — é assim que Locação e Serviços vão reaproveitar este modal.
   */
  setores?: Item[];
  gerando?: boolean;
  onGerar: (parametros: ParametrosRelatorio) => void;
}

/**
 * Os parâmetros de um relatório, perguntados na hora de gerá-lo.
 *
 * [29/09] Nasceu com o filtro de Setor do Relatório de Faturamento. O Setor NÃO foi para a
 * barra de filtros da tela de Nota Fiscal de propósito: ali ele pareceria filtrar o grid, e
 * não filtra — o grid continua listando todas as notas. Filtro que promete uma coisa e faz
 * outra sai mais caro que um clique a mais.
 *
 * O card pede que os três relatórios (Faturamento, Locação, Serviços) compartilhem
 * estrutura; por isso o período mora aqui e o que varia entra por propriedade.
 */
export const ModalRelatorio = ({
  open,
  onClose,
  titulo,
  dataInicioInicial,
  dataFimInicial,
  setores,
  gerando = false,
  onGerar,
}: Props) => {
  const formRef = useRef<FormikProps<any>>(null);

  const temSetor = (setores?.length ?? 0) > 0;

  const fields = [
    { name: 'dataInicio', type: 'date', label: 'Data Início', col: 6, required: true },
    { name: 'dataFim', type: 'date', label: 'Data Fim', col: 6, required: true },
    ...(temSetor
      ? [{
          name: 'setor',
          type: 'select',
          label: 'Setor',
          col: 12,
          // A primeira opção é o estado de sempre: sem setor escolhido, todos entram.
          options: [{ value: '', label: 'Todos os setores' }, ...(setores ?? [])],
        }]
      : []),
  ];

  const confirmar = (valores: any) => {
    if (!valores?.dataInicio || !valores?.dataFim) {
      return formRef.current?.setErrors({
        dataInicio: !valores?.dataInicio ? 'Informe a data inicial' : undefined,
        dataFim: !valores?.dataFim ? 'Informe a data final' : undefined,
      } as any);
    }

    if (valores.dataInicio > valores.dataFim) {
      return formRef.current?.setErrors({
        dataFim: 'A data final é anterior à inicial',
      } as any);
    }

    onGerar({
      dataInicio: valores.dataInicio,
      dataFim: valores.dataFim,
      // Vazio é "todos os setores", e vai como null: é o que a API entende e o que a
      // procedure trata como sem filtro. Zero seria ambíguo no log.
      setor: !valores.setor ? null : Number(valores.setor),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <ModalBox>
        <ModalH2>{titulo}</ModalH2>
        {/* A key remonta o formulário a cada abertura: quem fecha, muda as datas da tela e
            reabre tem de ver as novas, e não as da vez passada. */}
        <FormBuilder
          key={`${open}-${dataInicioInicial}-${dataFimInicial}`}
          fields={fields}
          initialValues={{
            dataInicio: dataInicioInicial ?? '',
            dataFim: dataFimInicial ?? '',
            setor: '',
          }}
          onSubmit={confirmar}
          gap='gap-3'
          innerRef={formRef}
        />
        <Footer>
          <Button onClick={onClose} color='error' disabled={gerando}>
            Fechar
          </Button>
          <Button
            type='button'
            variant='contained'
            color='primary'
            disabled={gerando}
            onClick={() => formRef.current?.submitForm()}
          >
            {gerando ? 'Gerando...' : 'Gerar'}
          </Button>
        </Footer>
      </ModalBox>
    </Modal>
  );
};
